import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { DEFAULT_FOOTER_SETTINGS } from '../data/menus';
import { useMenuConfig } from '../contexts/MenuConfigContext';
import { openCookieSettings } from '../utils/gdpr';
import {
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  Linkedin,
  Github,
  Mail } from
'lucide-react';
const WIDTH_CLASSES = {
  narrow: 'md:col-span-3',
  medium: 'md:col-span-4',
  wide: 'md:col-span-6',
  full: 'md:col-span-12'
};
const SOCIAL_ICONS: Record<string, React.ElementType> = {
  facebook: Facebook,
  instagram: Instagram,
  twitter: Twitter,
  youtube: Youtube,
  linkedin: Linkedin,
  github: Github,
  mail: Mail
};
export function SiteFooter() {
  const { lang, isRTL } = useLanguage();
  const menus = useMenuConfig().config;
  const isAr = lang === 'ar';
  const settings = menus.footerSettings || DEFAULT_FOOTER_SETTINGS;
  const currentYear = new Date().getFullYear().toString();
  const copyrightText = isAr ?
  settings.copyright.ar.replace('{year}', currentYear) :
  settings.copyright.en.replace('{year}', currentYear);
  return (
    <footer
      className="relative mt-24 text-white/90"
      style={{
        backgroundColor: settings.backgroundColor
      }}>
      
      {/* Subtle top accent line */}
      <div className="h-px w-full bg-gradient-to-r from-transparent via-white/15 to-transparent" />

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-20 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
          {/* Dynamic Footer Columns */}
          {menus.footerColumns.map((col) =>
          <div key={col.id} className={WIDTH_CLASSES[col.width || 'narrow']}>
              {col.contentType === 'brand' ?
            <div className="flex flex-col items-center md:items-start text-center md:text-start">
                  {col.brandLogoUrl &&
              <img
                src={col.brandLogoUrl}
                alt={isAr ? col.brandLogoAlt?.ar : col.brandLogoAlt?.en}
                className="w-auto h-14 sm:h-20 md:h-[var(--footer-logo-h)]"
                style={{
                  ['--footer-logo-h' as any]: `${col.brandLogoHeight || 96}px`,
                  filter: 'brightness(0) invert(1)'
                }} />

              }
                  {(col.brandTagline?.en || col.brandTagline?.ar) &&
              <p
                className={`mt-8 max-w-md text-base leading-relaxed text-white/55 ${isAr ? 'font-arabic' : 'font-serif italic'}`}>
                
                      {isAr ? col.brandTagline.ar : col.brandTagline.en}
                    </p>
              }
                </div> :

            <>
                  {(col.title.en || col.title.ar) &&
              <h4
                className={`text-[11px] uppercase tracking-[0.2em] font-semibold text-white/70 mb-5 ${isAr ? 'font-arabic tracking-normal' : ''}`}>
                
                      {isAr ? col.title.ar : col.title.en}
                    </h4>
              }

                  {col.contentType === 'links' &&
              <ul className="space-y-3">
                      {col.items.
                filter((item) => item.visible !== false).
                map((item) => {
                  const linkProps =
                  item.type === 'external' ?
                  {
                    href: item.url,
                    target: item.openInNewTab ?
                    '_blank' :
                    undefined,
                    rel: item.openInNewTab ?
                    'noopener noreferrer' :
                    undefined
                  } :
                  {
                    to: item.url
                  };
                  const LinkComponent =
                  item.type === 'external' ? 'a' : Link;
                  return (
                    <li key={item.id}>
                              <LinkComponent
                        {...linkProps as any}
                        className={`text-sm text-white/75 hover:text-white transition-colors ${isAr ? 'font-arabic' : ''}`}>
                        
                                {isAr ? item.label.ar : item.label.en}
                              </LinkComponent>
                            </li>);

                })}
                    </ul>
              }

                  {col.contentType === 'text' && col.content &&
              <div
                className={`text-sm text-white/75 leading-relaxed whitespace-pre-wrap ${isAr ? 'font-arabic' : ''}`}>
                
                      {isAr ? col.content.ar : col.content.en}
                    </div>
              }

                  {col.contentType === 'html' && col.content &&
              <div
                className={`text-sm text-white/75 leading-relaxed prose prose-invert prose-sm max-w-none ${isAr ? 'font-arabic' : ''}`}
                dangerouslySetInnerHTML={{
                  __html: isAr ? col.content.ar : col.content.en
                }} />

              }

                  {col.contentType === 'social' && col.socialLinks &&
              <div className="flex flex-wrap gap-4">
                      {col.socialLinks.map((social, idx) => {
                  const Icon =
                  SOCIAL_ICONS[social.platform.toLowerCase()] || Link;
                  return (
                    <a
                      key={idx}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/75 hover:bg-white/10 hover:text-white transition-all"
                      aria-label={social.platform}>
                      
                            <Icon size={18} />
                          </a>);

                })}
                    </div>
              }
                </>
            }
            </div>
          )}
        </div>

        {/* Bottom bar */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p
            className={`text-xs text-white/70 ${isAr ? 'font-arabic' : 'tracking-wide'}`}>
            
            {copyrightText}
          </p>
          <div className="flex items-center gap-5">
            <button
              type="button"
              onClick={openCookieSettings}
              className={`text-[11px] uppercase tracking-[0.2em] text-white/75 hover:text-white/80 transition-colors ${isAr ? 'font-arabic tracking-normal' : ''}`}>
              
              {isAr ? 'إدارة الكوكيز' : 'Manage cookies'}
            </button>
            {(settings.signoff.en || settings.signoff.ar) &&
            <p
              className={`text-[11px] uppercase tracking-[0.2em] text-white/65 ${isAr ? 'font-arabic tracking-normal' : ''}`}>
              
                {isAr ? settings.signoff.ar : settings.signoff.en}
              </p>
            }
          </div>
        </div>
      </div>
    </footer>);

}